import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  forwardRef,
} from 'react';
import { CheckType } from '../../../functions/checkType';

const isSingleStringArray = (value) =>
  Array.isArray(value) && value.length === 1 && typeof value[0] === 'string';

const wrapStringAsArray = (value) => [value];

const looksLikeStructuredJson = (value) => /^(?:[\[{"]|-?\d|true\b|false\b|null\b)/.test(value);

const readEditorText = (element) => (element?.innerText || '').replace(/\r\n/g, '\n');

const JSONField = forwardRef(
  (
    {
      fieldName,
      wrapperClass = '',
      label,
      hideLabel = false,
      allowNull = true,
      value = '',
      errors = [],
      disabled = false,
      helpText = '',
      placeholder = '',
      inputClass = '',
      onChange = () => {},
      pk = null,
    },
    ref
  ) => {
    const fieldId = pk ? `${pk}_${fieldName}` : fieldName;
    const editorRef = useRef(null);
    const lastLoadedValueRef = useRef(null);
    const [inputValue, setInputValue] = useState('');
    const [invalidJson, setInvalidJson] = useState(false);
    const [editorMessage, setEditorMessage] = useState('JSON or raw text');
    const [isFocused, setIsFocused] = useState(false);

    useImperativeHandle(ref, () => editorRef.current);

    const parseEditorValue = useCallback((nextText) => {
      const rawValue = nextText ?? '';
      const trimmed = rawValue.trim();

      if (trimmed === '') {
        return { kind: 'empty' };
      }

      try {
        const parsed = JSON.parse(trimmed);
        if (typeof parsed === 'string') {
          const normalized = wrapStringAsArray(parsed);
          return {
            kind: 'text',
            normalized,
            display: JSON.stringify(normalized),
            serialized: JSON.stringify(normalized),
          };
        }

        if (isSingleStringArray(parsed)) {
          return {
            kind: 'text',
            normalized: parsed,
            display: JSON.stringify(parsed),
            serialized: JSON.stringify(parsed),
          };
        }

        return {
          kind: 'json',
          normalized: parsed,
          display: JSON.stringify(parsed, null, 2),
          serialized: JSON.stringify(parsed),
        };
      } catch (error) {
        if (looksLikeStructuredJson(trimmed)) {
          return { kind: 'invalid' };
        }

        const normalized = wrapStringAsArray(rawValue);

        return {
          kind: 'text',
          normalized,
          display: JSON.stringify(normalized),
          serialized: JSON.stringify(normalized),
        };
      }
    }, []);

    const syncEditorDom = useCallback((nextText) => {
      if (editorRef.current && readEditorText(editorRef.current) !== nextText) {
        editorRef.current.textContent = nextText;
      }
    }, []);

    const formatForDisplay = useCallback(
      (nextValue) => {
        if (nextValue === null || typeof nextValue === 'undefined') {
          return '';
        }

        const type = CheckType(nextValue);

        if (type === 'Array') {
          if (isSingleStringArray(nextValue)) {
            return JSON.stringify(nextValue);
          }

          try {
            return JSON.stringify(nextValue, null, 2);
          } catch (error) {
            return String(nextValue);
          }
        }

        if (type === 'Object') {
          try {
            return JSON.stringify(nextValue, null, 2);
          } catch (error) {
            return String(nextValue);
          }
        }

        const parsed = parseEditorValue(String(nextValue));
        return parsed.display ?? String(nextValue);
      },
      [parseEditorValue]
    );

    const updateEditorState = useCallback(
      (nextText, shouldEmit = true) => {
        setInputValue(nextText);
        syncEditorDom(nextText);

        const parsed = parseEditorValue(nextText);

        if (parsed.kind === 'empty') {
          setInvalidJson(false);
          setEditorMessage('Empty value');

          if (shouldEmit) {
            onChange({ name: fieldName, value: allowNull ? null : '', label: '' });
          }

          return;
        }

        if (parsed.kind === 'invalid') {
          setInvalidJson(true);
          setEditorMessage('Invalid JSON structure');
          return;
        }

        setInvalidJson(false);
        setEditorMessage(parsed.kind === 'json' ? 'Valid JSON' : 'Text normalized to single-item array');

        if (shouldEmit) {
          onChange({
            name: fieldName,
            value: parsed.serialized,
            label: parsed.serialized,
          });
        }
      },
      [allowNull, fieldName, onChange, parseEditorValue, syncEditorDom]
    );

    const updateValidationState = useCallback(
      (nextText) => {
        setInputValue(nextText);

        const parsed = parseEditorValue(nextText);

        if (parsed.kind === 'empty') {
          setInvalidJson(false);
          setEditorMessage('Empty value');
          return;
        }

        if (parsed.kind === 'invalid') {
          setInvalidJson(true);
          setEditorMessage('Invalid JSON structure');
          return;
        }

        setInvalidJson(false);
        setEditorMessage(parsed.kind === 'json' ? 'Valid JSON' : 'Text normalized to single-item array');
      },
      [parseEditorValue]
    );

    const insertTextAtCursor = useCallback((text) => {
      if (!document.queryCommandSupported || !document.queryCommandSupported('insertText')) {
        return false;
      }

      return document.execCommand('insertText', false, text);
    }, []);

    const handleInput = useCallback(
      (event) => {
        updateValidationState(readEditorText(event.currentTarget));
      },
      [updateValidationState]
    );

    const handlePaste = useCallback(
      (event) => {
        event.preventDefault();
        const pastedText = event.clipboardData?.getData('text/plain') || '';

        if (!insertTextAtCursor(pastedText)) {
          updateValidationState(`${inputValue}${pastedText}`);
        }
      },
      [inputValue, insertTextAtCursor, updateValidationState]
    );

    const handleKeyDown = useCallback(
      (event) => {
        if (event.key === 'Tab') {
          event.preventDefault();
          if (!insertTextAtCursor('  ')) {
            updateValidationState(`${inputValue}  `);
          }
        }
      },
      [inputValue, insertTextAtCursor, updateValidationState]
    );

    const handleValidate = useCallback(() => {
      const parsed = parseEditorValue(inputValue);

      if (parsed.kind === 'invalid') {
        setInvalidJson(true);
        setEditorMessage('Invalid JSON structure');
        return;
      }

      setInvalidJson(false);
      setEditorMessage(parsed.kind === 'json' ? 'Valid JSON' : 'Text normalized to single-item array');
    }, [inputValue, parseEditorValue]);

    const handleCopy = useCallback(async () => {
      if (!navigator.clipboard) {
        return;
      }

      await navigator.clipboard.writeText(inputValue);
      setEditorMessage('Copied to clipboard');
    }, [inputValue]);

    useEffect(() => {
      const nextDisplayValue = formatForDisplay(value);

      if (nextDisplayValue === lastLoadedValueRef.current) {
        return;
      }

      lastLoadedValueRef.current = nextDisplayValue;

      if (isFocused) {
        return;
      }

      updateEditorState(nextDisplayValue, false);
    }, [fieldId, formatForDisplay, isFocused, updateEditorState, value]);

    const lineCount = inputValue === '' ? 1 : inputValue.split('\n').length;

    return (
      <>
        <div
          className={`${wrapperClass} ${errors.length > 0 || invalidJson ? 'is-invalid has-error' : ''}`}
          style={{
            border: '1px solid',
            borderColor: errors.length > 0 || invalidJson ? '#dc3545' : '#ced4da',
            borderRadius: '0.375rem',
            overflow: 'hidden',
            backgroundColor: disabled ? '#f8f9fa' : '#fff',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '0.75rem',
              padding: '2px 0.5rem',
              borderBottom: '1px solid #e9ecef',
              backgroundColor: '#f8f9fa',
              flexWrap: 'wrap',
            }}
          >
            <small style={{ color: invalidJson ? '#dc3545' : '#6c757d' }}>{editorMessage}</small>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <button type="button" className="btn btn-sm py-1 btn-light" onClick={handleValidate} disabled={disabled}>
                Validate
              </button>
              <button type="button" className="btn btn-sm py-1 btn-light" onClick={handleCopy} disabled={disabled}>
                Copy
              </button>
            </div>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '48px 1fr',
              minHeight: '70px',
              maxHeight: '420px',
            }}
          >
            <div
              aria-hidden="true"
              style={{
                padding: '0.75rem 0.5rem',
                textAlign: 'right',
                backgroundColor: '#f8f9fa',
                borderRight: '1px solid #e9ecef',
                color: '#adb5bd',
                fontFamily: 'SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                fontSize: '0.875rem',
                lineHeight: '1.5',
                userSelect: 'none',
                overflow: 'hidden',
              }}
            >
              {Array.from({ length: lineCount }, (_, index) => (
                <div key={`${fieldId}_line_${index + 1}`}>{index + 1}</div>
              ))}
            </div>

            <div
              key={fieldId}
              id={fieldId}
              ref={editorRef}
              name={fieldName}
              role="textbox"
              aria-multiline="true"
              contentEditable={disabled ? false : 'plaintext-only'}
              suppressContentEditableWarning={true}
              spellCheck={false}
              data-placeholder={placeholder}
              onFocus={() => setIsFocused(true)}
              onBlur={(event) => {
                setIsFocused(false);
                updateEditorState(readEditorText(event.currentTarget), true);
              }}
              onInput={handleInput}
              onPaste={handlePaste}
              onKeyDown={handleKeyDown}
              className={inputClass}
              style={{
                padding: '0.75rem 1rem',
                whiteSpace: 'pre-wrap',
                overflowWrap: 'anywhere',
                overflow: 'auto',
                outline: 'none',
                fontFamily: 'SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                fontSize: '0.875rem',
                lineHeight: '1.5',
              }}
            />
          </div>
        </div>

        <div className="d-flex justify-content-between mt-1">
          <small className="text-muted">
            {lineCount} line{lineCount === 1 ? '' : 's'}
          </small>
          <small className="text-muted">{inputValue.length} chars</small>
        </div>

        {invalidJson && <div className="invalid-feedback d-block">Invalid JSON</div>}
      </>
    );
  }
);

export default JSONField;
